// src/components/CandidatesPage.tsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  UserCheck,
  Users,
  RefreshCw,
  Filter,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Star,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  Phone,
  Mail,
  FileText,
  TrendingUp,
  ArrowRight,
  Trash2,
  Briefcase,
  Search,
  BarChart3,
  Plus,
  X,
} from 'lucide-react';
import {
  getCandidates,
  getCandidateStats,
  getVacancies,
  updateCandidateStatus,
  deleteCandidate,
  createCandidate,
  downloadCandidateCV,
  type CandidateDTO,
  type RecruitmentStats,
  type VacancyDTO,
} from '@/lib/api';

/* ─── Status pipeline config ─── */
const STATUS_CFG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode; order: number }> = {
  received:   { label: 'Recibido',   color: 'text-gray-400',    bg: 'bg-gray-500/20',    icon: <Clock size={14} />, order: 0 },
  screening:  { label: 'Screening',  color: 'text-blue-400',    bg: 'bg-blue-500/20',    icon: <Eye size={14} />, order: 1 },
  evaluated:  { label: 'Evaluado',   color: 'text-purple-400',  bg: 'bg-purple-500/20',  icon: <Star size={14} />, order: 2 },
  interview:  { label: 'Entrevista', color: 'text-amber-400',   bg: 'bg-amber-500/20',   icon: <Users size={14} />, order: 3 },
  offer:      { label: 'Oferta',     color: 'text-emerald-400', bg: 'bg-emerald-500/20',  icon: <TrendingUp size={14} />, order: 4 },
  hired:      { label: 'Contratado', color: 'text-green-400',   bg: 'bg-green-500/20',    icon: <CheckCircle2 size={14} />, order: 5 },
  rejected:   { label: 'Rechazado',  color: 'text-red-400',     bg: 'bg-red-500/20',      icon: <XCircle size={14} />, order: 6 },
  withdrawn:  { label: 'Retirado',   color: 'text-red-300',     bg: 'bg-red-400/20',      icon: <XCircle size={14} />, order: 7 },
};

/* Valid transitions for each status */
const VALID_TRANSITIONS: Record<string, string[]> = {
  received:  ['screening', 'rejected', 'withdrawn'],
  screening: ['evaluated', 'rejected', 'withdrawn'],
  evaluated: ['interview', 'rejected', 'withdrawn'],
  interview: ['offer', 'rejected', 'withdrawn'],
  offer:     ['hired', 'rejected', 'withdrawn'],
  hired:     [],
  rejected:  [],
  withdrawn: [],
};

/* Score colors */
function scoreColor(score: number | null): string {
  if (score === null) return 'text-gray-500';
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-emerald-400';
  if (score >= 40) return 'text-amber-400';
  return 'text-red-400';
}

function scoreBadge(score: number | null): string {
  if (score === null) return 'Sin evaluar';
  if (score >= 80) return 'Excelente';
  if (score >= 60) return 'Buen match';
  if (score >= 40) return 'Parcial';
  return 'Bajo';
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* ─── Create candidate modal ─── */
function CreateCandidateModal({
  vacancies,
  onClose,
  onCreated,
}: {
  vacancies: VacancyDTO[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [vacancyId, setVacancyId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !vacancyId) return;
    setSaving(true);
    setError(null);
    try {
      await createCandidate({
        vacancy_id: vacancyId,
        candidate_name: name.trim(),
        candidate_phone: phone.trim() || undefined,
        candidate_email: email.trim() || undefined,
        source: 'dashboard',
      });
      onCreated();
    } catch (e: any) {
      setError(e.message || 'Error al crear candidato');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[#1a1a2e] border border-white/10 rounded-xl p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold text-lg">Nuevo Candidato</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg text-sm">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-white/60 text-xs block mb-1">Vacante *</label>
            <select
              value={vacancyId}
              onChange={(e) => setVacancyId(e.target.value)}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none"
            >
              <option value="">Seleccionar vacante...</option>
              {vacancies.filter(v => v.status === 'open').map(v => (
                <option key={v.id} value={v.id}>{v.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-white/60 text-xs block mb-1">Nombre *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Nombre completo del candidato"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="text-white/60 text-xs block mb-1">Teléfono</label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+52 55 1234 5678"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="text-white/60 text-xs block mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="candidato@email.com"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-white/60 hover:text-white text-sm">Cancelar</button>
            <button
              type="submit"
              disabled={saving || !name.trim() || !vacancyId}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
            >
              {saving ? 'Creando...' : 'Crear Candidato'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Main Component ─── */
export function CandidatesPage() {
  const [applications, setApplications] = useState<CandidateDTO[]>([]);
  const [stats, setStats] = useState<RecruitmentStats | null>(null);
  const [vacancies, setVacancies] = useState<VacancyDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'pipeline'>('pipeline');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [candData, statsData, vacData] = await Promise.all([
        getCandidates({ status: statusFilter || undefined, limit: 100 }),
        getCandidateStats(),
        getVacancies({ limit: 100 }),
      ]);
      setApplications(candData.applications || []);
      setStats(statsData);
      setVacancies(vacData.vacancies || []);
    } catch (e: any) {
      setError(e.message || 'Error al cargar candidatos');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id: string) => setExpandedId(prev => prev === id ? null : id);

  const handleStatusChange = async (id: string, newStatus: string) => {
    setActionLoading(id);
    try {
      await updateCandidateStatus(id, newStatus);
      await load();
    } catch (e: any) {
      setError(e.message || 'Error al cambiar estado');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Eliminar esta aplicación?')) return;
    setActionLoading(id);
    try {
      await deleteCandidate(id);
      await load();
    } catch (e: any) {
      setError(e.message || 'Error al eliminar');
    } finally {
      setActionLoading(null);
    }
  };

  // Map vacancy IDs to titles
  const vacancyMap = new Map(vacancies.map(v => [v.id, v.title]));

  // Filter by search term
  const filtered = applications.filter(a => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      a.candidate_name.toLowerCase().includes(s) ||
      (a.candidate_email || '').toLowerCase().includes(s) ||
      (a.candidate_phone || '').includes(s) ||
      (vacancyMap.get(a.vacancy_id) || '').toLowerCase().includes(s)
    );
  });

  /* ─── Pipeline view ─── */
  const pipelineStatuses = ['received', 'screening', 'evaluated', 'interview', 'offer', 'hired'];

  const grouped = pipelineStatuses.reduce((acc, st) => {
    acc[st] = filtered.filter(a => a.status === st);
    return acc;
  }, {} as Record<string, CandidateDTO[]>);

  // Also count rejected/withdrawn separately
  const finalizedCount = filtered.filter(a => a.status === 'rejected' || a.status === 'withdrawn').length;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <UserCheck className="text-purple-400" />
            Pipeline de Reclutamiento
          </h1>
          <p className="text-white/60 mt-1">Gestión y seguimiento de candidatos</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm transition-colors"
          >
            <Plus size={14} />
            Nuevo Candidato
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Recargar
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'Total', val: stats.total_applications, color: 'text-blue-400' },
            { label: 'En proceso', val: Object.entries(stats.by_status).filter(([k]) => !['hired', 'rejected', 'withdrawn'].includes(k)).reduce((s, [, v]) => s + v, 0), color: 'text-amber-400' },
            { label: 'Contratados', val: stats.by_status.hired || 0, color: 'text-green-400' },
            { label: 'Rechazados', val: (stats.by_status.rejected || 0) + (stats.by_status.withdrawn || 0), color: 'text-red-400' },
            { label: 'Score prom.', val: stats.avg_ai_score !== null ? stats.avg_ai_score.toFixed(0) : '—', color: 'text-purple-400' },
          ].map(s => (
            <div key={s.label} className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{s.val}</div>
              <div className="text-white/60 text-xs mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Controls Row */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar candidato, email, vacante..."
            className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-blue-500 outline-none"
          />
        </div>

        {/* View toggle */}
        <div className="flex bg-white/5 border border-white/10 rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode('pipeline')}
            className={`px-3 py-2 text-xs font-medium transition-colors ${viewMode === 'pipeline' ? 'bg-blue-500 text-white' : 'text-white/60 hover:text-white'}`}
          >
            <BarChart3 size={14} className="inline mr-1" />
            Pipeline
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-2 text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'text-white/60 hover:text-white'}`}
          >
            <FileText size={14} className="inline mr-1" />
            Lista
          </button>
        </div>

        {/* Status filter (list view only) */}
        {viewMode === 'list' && (
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-white/40" />
            {['', 'received', 'screening', 'evaluated', 'interview', 'offer', 'hired', 'rejected'].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-blue-500 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                {s ? (STATUS_CFG[s]?.label || s) : 'Todos'}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300"><X size={14} /></button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <RefreshCw size={24} className="animate-spin mx-auto text-purple-400" />
          <p className="text-white/60 mt-2">Cargando candidatos...</p>
        </div>
      )}

      {/* Empty */}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 bg-white/5 border border-white/10 rounded-lg">
          <UserCheck size={48} className="mx-auto text-white/20 mb-4" />
          <h3 className="text-white text-lg font-medium">No hay candidatos</h3>
          <p className="text-white/60 mt-1">
            {searchTerm
              ? `No se encontraron candidatos para "${searchTerm}"`
              : statusFilter
                ? `No hay candidatos con estado "${STATUS_CFG[statusFilter]?.label || statusFilter}"`
                : 'Aún no hay aplicaciones registradas'}
          </p>
        </div>
      )}

      {/* ─── Pipeline View ─── */}
      {!loading && viewMode === 'pipeline' && filtered.length > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            {pipelineStatuses.map(st => {
              const cfg = STATUS_CFG[st];
              const items = grouped[st] || [];
              return (
                <div key={st} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  {/* Column header */}
                  <div className={`px-3 py-2 border-b border-white/10 flex items-center justify-between ${cfg.bg}`}>
                    <div className="flex items-center gap-2">
                      <span className={cfg.color}>{cfg.icon}</span>
                      <span className={`text-xs font-semibold ${cfg.color}`}>{cfg.label}</span>
                    </div>
                    <span className="text-white/50 text-xs font-mono">{items.length}</span>
                  </div>
                  {/* Column cards */}
                  <div className="p-2 space-y-2 max-h-[500px] overflow-y-auto">
                    {items.length === 0 && (
                      <div className="text-center py-4 text-white/30 text-xs">Vacío</div>
                    )}
                    {items.map(app => (
                      <PipelineCard
                        key={app.id}
                        app={app}
                        vacancyTitle={vacancyMap.get(app.vacancy_id) || 'Vacante'}
                        onStatusChange={handleStatusChange}
                        actionLoading={actionLoading}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Finalized row */}
          {finalizedCount > 0 && (
            <div className="text-center text-white/40 text-xs py-2">
              + {finalizedCount} candidato{finalizedCount !== 1 ? 's' : ''} rechazado{finalizedCount !== 1 ? 's' : ''}/retirado{finalizedCount !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      {/* ─── List View ─── */}
      {!loading && viewMode === 'list' && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map(app => {
            const sc = STATUS_CFG[app.status] || STATUS_CFG.received;
            const isExpanded = expandedId === app.id;
            const nextStatuses = VALID_TRANSITIONS[app.status] || [];

            return (
              <div key={app.id} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-colors">
                {/* Card header */}
                <button type="button" onClick={() => toggle(app.id)} className="w-full text-left p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="text-white font-semibold">{app.candidate_name}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                          {sc.icon} {sc.label}
                        </span>
                        {app.ai_score !== null && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-white/10 ${scoreColor(app.ai_score)}`}>
                            <Star size={12} /> {app.ai_score.toFixed(0)} — {scoreBadge(app.ai_score)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-white/60 text-sm flex-wrap mt-1">
                        <span className="flex items-center gap-1">
                          <Briefcase size={13} /> {vacancyMap.get(app.vacancy_id) || 'Sin vacante'}
                        </span>
                        {app.candidate_email && (
                          <span className="flex items-center gap-1"><Mail size={13} /> {app.candidate_email}</span>
                        )}
                        {app.candidate_phone && (
                          <span className="flex items-center gap-1"><Phone size={13} /> {app.candidate_phone}</span>
                        )}
                        <span className="flex items-center gap-1"><Clock size={13} /> {formatDate(app.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 mt-1">
                      {isExpanded ? <ChevronUp size={18} className="text-white/40" /> : <ChevronDown size={18} className="text-white/40" />}
                    </div>
                  </div>
                </button>

                {/* Expanded */}
                {isExpanded && (
                  <div className="border-t border-white/10 p-5 bg-black/20 space-y-4">
                    {/* AI Summary */}
                    {app.ai_summary && (
                      <div>
                        <h4 className="text-white/80 font-medium text-sm mb-2 flex items-center gap-2">
                          <Star size={14} className="text-purple-400" /> Evaluación IA
                        </h4>
                        <p className="text-white/70 text-sm whitespace-pre-wrap">{app.ai_summary}</p>
                      </div>
                    )}

                    {/* AI Extracted Data */}
                    {app.ai_extracted_data && (
                      <div>
                        <h4 className="text-white/80 font-medium text-sm mb-2">Datos Extraídos</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {Object.entries(app.ai_extracted_data).map(([k, v]) => (
                            <div key={k} className="bg-white/5 rounded-lg px-3 py-2">
                              <span className="text-white/50 text-xs capitalize">{k.replace(/_/g, ' ')}</span>
                              <div className="text-white/80 text-sm mt-0.5">
                                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Stage History */}
                    {app.stage_history && app.stage_history.length > 0 && (
                      <div>
                        <h4 className="text-white/80 font-medium text-sm mb-2">Historial</h4>
                        <div className="space-y-1">
                          {app.stage_history.map((entry, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-white/60">
                              <ArrowRight size={10} />
                              <span className="font-medium">{STATUS_CFG[entry.to]?.label || entry.to}</span>
                              {entry.notes && <span className="text-white/40">— {entry.notes}</span>}
                              {entry.timestamp && <span className="text-white/30 ml-auto">{formatDate(entry.timestamp)}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-white/10">
                      {nextStatuses.length > 0 && (
                        <>
                          <span className="text-white/40 text-xs mr-1">Mover a:</span>
                          {nextStatuses.map(ns => {
                            const ncfg = STATUS_CFG[ns];
                            return (
                              <button
                                key={ns}
                                onClick={() => handleStatusChange(app.id, ns)}
                                disabled={actionLoading === app.id}
                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${ncfg.bg} ${ncfg.color} hover:opacity-80 disabled:opacity-50`}
                              >
                                {actionLoading === app.id ? (
                                  <RefreshCw size={12} className="animate-spin" />
                                ) : (
                                  ncfg.icon
                                )}
                                {ncfg.label}
                              </button>
                            );
                          })}
                        </>
                      )}
                      {/* CV Download Button */}
                      {app.cv_resource_id && (
                        <button
                          onClick={async () => {
                            try {
                              await downloadCandidateCV(app.id, app.candidate_name);
                            } catch (err: any) {
                              alert(err.message || 'Error al descargar CV');
                            }
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
                        >
                          <FileText size={12} /> Descargar CV
                        </button>
                      )}
                      {(app.status === 'received' || app.status === 'withdrawn') && (
                        <button
                          onClick={() => handleDelete(app.id)}
                          disabled={actionLoading === app.id}
                          className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 disabled:opacity-50 transition-colors"
                        >
                          <Trash2 size={12} /> Eliminar
                        </button>
                      )}
                    </div>

                    {/* Meta info */}
                    <div className="flex items-center gap-4 text-white/50 text-xs">
                      <span>Fuente: {app.source}</span>
                      {app.evaluated_at && <span>Evaluado: {formatDate(app.evaluated_at)}</span>}
                      {app.assigned_recruiter_id && <span>Reclutador asignado</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateCandidateModal
          vacancies={vacancies}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            load();
          }}
        />
      )}
    </div>
  );
}

/* ─── Pipeline Card (small card for pipeline columns) ─── */
function PipelineCard({
  app,
  vacancyTitle,
  onStatusChange,
  actionLoading,
}: {
  app: CandidateDTO;
  vacancyTitle: string;
  onStatusChange: (id: string, status: string) => void;
  actionLoading: string | null;
}) {
  const [showActions, setShowActions] = useState(false);
  const nextStatuses = VALID_TRANSITIONS[app.status] || [];

  return (
    <div
      className="bg-black/30 border border-white/10 rounded-lg p-3 hover:border-white/20 transition-colors cursor-pointer"
      onClick={() => setShowActions(!showActions)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-white text-sm font-medium truncate">{app.candidate_name}</div>
          <div className="text-white/50 text-xs truncate mt-0.5">{vacancyTitle}</div>
        </div>
        {app.ai_score !== null && (
          <div className={`flex-shrink-0 text-sm font-bold ${scoreColor(app.ai_score)}`}>
            {app.ai_score.toFixed(0)}
          </div>
        )}
      </div>

      {/* Contact info */}
      <div className="mt-2 space-y-0.5">
        {app.candidate_email && (
          <div className="text-white/40 text-xs truncate flex items-center gap-1">
            <Mail size={10} /> {app.candidate_email}
          </div>
        )}
        {app.candidate_phone && (
          <div className="text-white/40 text-xs flex items-center gap-1">
            <Phone size={10} /> {app.candidate_phone}
          </div>
        )}
      </div>

      {/* Quick actions */}
      {showActions && nextStatuses.length > 0 && (
        <div className="mt-2 pt-2 border-t border-white/10 flex flex-wrap gap-1">
          {nextStatuses.map(ns => {
            const ncfg = STATUS_CFG[ns];
            return (
              <button
                key={ns}
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(app.id, ns);
                }}
                disabled={actionLoading === app.id}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${ncfg.bg} ${ncfg.color} hover:opacity-80 disabled:opacity-50`}
              >
                {actionLoading === app.id ? <RefreshCw size={10} className="animate-spin" /> : ncfg.icon}
                {ncfg.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Date */}
      <div className="text-white/30 text-[10px] mt-2">{formatDate(app.created_at)}</div>
    </div>
  );
}

export default CandidatesPage;
